import { Component } from 'react';
import './JxLineEdit.css';
import CoreComponent from '../../services/engines/CoreComponent';

/**
 * JxLineEdit — themed text input with a built-in maxLength check.
 * Pure painter — caller owns the value via onChange, no internal state.
 *
 * Subclasses can override getValue(), getPrefix(), getSuffix(), getPlaceholder(), getPropsToOmit(),
 * handleChange() and handleKeyDown() to inject custom behaviour
 * without duplicating the render tree (e.g. JxSearchEdit, CmdBarEdit, FilterEdit).
 *
 * @param {string} id - explicit DOM id, e.g. `${_name}_<field>`
 * @param {string} value
 * @param {(value: string) => void} onChange
 * @param {number} [maxLength] - when set, shows a live counter under the field and hard-caps input
 * @param {string} [placeholder]
 * @param {string} [error] - error message; when present, shows red border + message below the field
 * @param {boolean} disabled
 * @param {React.ReactNode} [suffix] - icon/button rendered inside the input box, right side
 * @param {() => void} [onEnter] - called when user presses Enter
 * @param {() => void} [onEscape] - called after input is cleared on Escape
 */
export default class JxLineEdit extends CoreComponent {
    // ── getter hooks — override in subclass to inject value / prefix / suffix / placeholder / strip props ──

    /** Returns the current display value. Subclass overrides to use internal state. */
    getValue() {
        return this.props.value ?? '';
    }

    /** Returns the prefix node rendered inside the input box, left side. Subclass overrides to inject an icon etc. */
    getPrefix() {
        return null;
    }

    /** Returns the suffix node. Subclass overrides to inject a clear button etc. */
    getSuffix() {
        return this.props.suffix;
    }

    /** Returns the placeholder string. Subclass overrides to inject dynamic hints. */
    getPlaceholder() {
        return this.props.placeholder;
    }

    /**
     * Returns prop keys to strip from ...rest before spreading onto <input>.
     * Subclass overrides to prevent unknown DOM prop warnings for their own props.
     */
    getPropsToOmit() {
        return [];
    }

    // ── handlers — override in subclass ──

    handleChange(e) {
        const next = e.target.value;
        const { maxLength, onChange } = this.props;
        if (maxLength && next.length > maxLength) return;
        onChange(next);
    }

    handleKeyDown(e) {
        const { onEnter, onEscape, onChange } = this.props;
        if (e.key === 'Enter' && onEnter) {
            e.preventDefault();
            onEnter();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            // e.stopPropagation(); // prevent JxForm from catching it
            onChange?.(''); // clear input — internal behaviour
            onEscape?.(); // notify consumer if interested
        }
    }

    // ── render ──

    render() {
        // Destructure known props — remainder goes to <input> via domProps.
        // getPropsToOmit() lets subclasses strip their own custom props before spreading.
        const { id, value, maxLength, placeholder, error, disabled = false, suffix, onChange, onEnter, onEscape, ...rest } = this.props;

        const omit = new Set(this.getPropsToOmit());
        const domProps = Object.fromEntries(
            Object.entries(rest).filter(function keepProp([k]) {
                return !omit.has(k);
            }),
        );

        const val = this.getValue();
        const pfx = this.getPrefix();
        const sfx = this.getSuffix();
        const ph = this.getPlaceholder();

        return (
            <div className="jx-line-edit-wrap">
                <div className="jx-line-edit-box">
                    {pfx && <div className="jx-line-edit-prefix">{pfx}</div>}
                    <input
                        id={id}
                        type="text"
                        autoComplete="off"
                        className={[
                            'jx-line-edit',
                            error ? 'jx-line-edit-error' : '',
                            pfx ? 'jx-line-edit-has-prefix' : '',
                            sfx ? 'jx-line-edit-has-suffix' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        value={val}
                        onChange={(e) => this.handleChange(e)}
                        onKeyDown={(e) => this.handleKeyDown(e)}
                        placeholder={ph}
                        disabled={disabled}
                        {...domProps}
                    />
                    {sfx && <div className="jx-line-edit-suffix">{sfx}</div>}
                </div>
                {maxLength && (
                    <span className="jx-line-edit-counter">
                        {val.length}/{maxLength}
                    </span>
                )}
                {error && <span className="jx-line-edit-error-text">{error}</span>}
            </div>
        );
    }
}
